import { useMemo } from 'react';

import { useAuth } from '../context/AuthContext';
import { apiDelete, apiFetch, apiPatch, apiPost, apiPostForm, apiPut, extractArray } from '../lib/api';

/**
 * API client for the built-in Wiki V2 contract. Keeping the client on top of
 * the shared api helpers means auth headers, same-origin routing, and error
 * envelopes stay consistent with the rest of the runtime SPA.
 */
export function createWikiClient(getAuthHeader) {
    const headers = () => (typeof getAuthHeader === 'function' ? getAuthHeader() : {});
    return {
        get: (path) => apiFetch(path, headers()),
        post: (path, body = {}) => apiPost(path, body, headers()),
        postForm: (path, formData) => apiPostForm(path, formData, headers()),
        put: (path, body = {}) => apiPut(path, body, headers()),
        patch: (path, body = {}) => apiPatch(path, body, headers()),
        delete: (path) => apiDelete(path, headers()),
    };
}

export function useWikiV2Client() {
    const { getAuthHeader } = useAuth();
    return useMemo(() => createWikiClient(getAuthHeader), [getAuthHeader]);
}

function extractArrayValue(data, key) {
    if (Array.isArray(data)) return data;
    const value = data?.[key] || data?.items || data?.data || [];
    return Array.isArray(value) ? value : [];
}

/**
 * The API intentionally returns a flat, ACL-filtered category list. Build the
 * two-level tree in one place so the sidebar and editor use the same shape.
 * New Wiki code should prefer `useWikiCategories()` / `indexWikiCategories`
 * (pages/wiki/shared) which also index by id and resolve subcategories.
 */
export function extractWikiCategories(data) {
    const categories = extractArrayValue(data, 'categories');
    if (categories.some(category => Array.isArray(category?.children) || Array.isArray(category?.subcategories))) {
        return categories;
    }

    const byParent = new Map();
    categories.forEach((category) => {
        const parentId = category?.parent_id || null;
        const key = parentId || '__root__';
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push({ ...category, children: [] });
    });
    const attach = (category) => {
        const id = wikiId(category);
        const children = byParent.get(id) || [];
        return { ...category, children: children.map(attach) };
    };
    return (byParent.get('__root__') || []).map(attach);
}

export function extractWikiPages(data) {
    return extractArrayValue(data, 'pages');
}

export function extractWikiSearchHits(data) {
    if (Array.isArray(data)) return data;
    const value = data?.hits || data?.results || data?.items || data?.data || [];
    return Array.isArray(value) ? value : [];
}

export function extractWikiVersions(data) {
    return extractArrayValue(data, 'versions');
}

export function wikiId(value, fallback = '') {
    return value?.category_id || value?.page_id || value?.version_id || value?.id || fallback;
}

export function wikiCategoryChildren(category) {
    return category?.subcategories || category?.children || [];
}

export function wikiPageTitle(page) {
    return page?.title || page?.name || 'Untitled page';
}

export function wikiPageBody(page) {
    if (page?.status === 'draft') return page?.draft_md ?? page?.body_md ?? '';
    return page?.body_md ?? page?.draft_md ?? '';
}

/**
 * The machine code of a Wiki API error: `detail.code` for structured errors
 * (`{code, limit, used}` from the plan limits), the bare string for
 * `HTTPException(detail="wiki_page_not_found")`, else `code`, else ''.
 */
export function wikiErrorCode(error) {
    const data = error?.response?.data;
    const detail = data?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) return detail.code;
    if (typeof detail === 'string' && /^[a-z0-9_.:-]+$/.test(detail)) return detail;
    return data?.code || '';
}

/** HTTP status of a Wiki API error (0 when there was no response). */
export function wikiErrorStatus(error) {
    return Number(error?.response?.status) || 0;
}

// Shared query option used by all Wiki reads. The category rail intentionally
// stays fresh for two minutes, matching the runtime query-client convention.
export const WIKI_QUERY_DEFAULTS = {
    staleTime: 2 * 60 * 1000,
    retry: 1,
};

export default useWikiV2Client;

// Keep extractArray available to consumers that need a generic paginated read
// without importing another API module.
export { extractArray };
